// src/services/viacep.ts
import { onlyDigits } from "@/lib/masks";

export type ViaCepResponse = {
  cep?: string;
  logradouro?: string;
  complemento?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
  erro?: boolean;
};

/** Busca endereço por CEP direto */
export async function fetchViaCep(cep: string): Promise<ViaCepResponse | null> {
  const clean = onlyDigits(cep).slice(0, 8);
  if (clean.length !== 8) return null;

  try {
    const res = await fetch(`https://viacep.com.br/ws/${clean}/json/`, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) return null;

    const data = (await res.json().catch(() => null)) as ViaCepResponse | null;
    if (!data || data.erro) return null;

    return data;
  } catch {
    return null;
  }
}

/** Busca CEPs por UF + Cidade + Logradouro (mín 3 chars cada) */
export async function searchViaCep(
  uf: string,
  cidade: string,
  logradouro: string,
): Promise<ViaCepResponse[]> {
  const ufClean = uf.trim().toUpperCase();
  const cidadeClean = cidade.trim();
  const logClean = logradouro.trim();

  if (ufClean.length < 2 || cidadeClean.length < 3 || logClean.length < 3) return [];

  try {
    const url = `https://viacep.com.br/ws/${encodeURIComponent(ufClean)}/${encodeURIComponent(cidadeClean)}/${encodeURIComponent(logClean)}/json/`;
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
    });

    if (!res.ok) return [];

    const data = await res.json().catch(() => null);
    if (!data || !Array.isArray(data)) return [];

    return data as ViaCepResponse[];
  } catch {
    return [];
  }
}

/** Lista de UFs brasileiras em ordem alfabética */
export const BRAZILIAN_STATES = [
  { value: "AC", label: "AC - Acre" },
  { value: "AL", label: "AL - Alagoas" },
  { value: "AP", label: "AP - Amapá" },
  { value: "AM", label: "AM - Amazonas" },
  { value: "BA", label: "BA - Bahia" },
  { value: "CE", label: "CE - Ceará" },
  { value: "DF", label: "DF - Distrito Federal" },
  { value: "ES", label: "ES - Espírito Santo" },
  { value: "GO", label: "GO - Goiás" },
  { value: "MA", label: "MA - Maranhão" },
  { value: "MT", label: "MT - Mato Grosso" },
  { value: "MS", label: "MS - Mato Grosso do Sul" },
  { value: "MG", label: "MG - Minas Gerais" },
  { value: "PA", label: "PA - Pará" },
  { value: "PB", label: "PB - Paraíba" },
  { value: "PR", label: "PR - Paraná" },
  { value: "PE", label: "PE - Pernambuco" },
  { value: "PI", label: "PI - Piauí" },
  { value: "RJ", label: "RJ - Rio de Janeiro" },
  { value: "RN", label: "RN - Rio Grande do Norte" },
  { value: "RS", label: "RS - Rio Grande do Sul" },
  { value: "RO", label: "RO - Rondônia" },
  { value: "RR", label: "RR - Roraima" },
  { value: "SC", label: "SC - Santa Catarina" },
  { value: "SP", label: "SP - São Paulo" },
  { value: "SE", label: "SE - Sergipe" },
  { value: "TO", label: "TO - Tocantins" },
] as const;
